import { useEffect, useMemo, useState } from 'react';

import { ChevronRightIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Skeleton } from 'components/Skeleton';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { SuperDispatchLaneRecord } from 'lib/api/getQuickQuote';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import { MaybeUndef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { generatePageNumbers } from 'utils/generatePageNumbers';
import { cn } from 'utils/shadcn';

import {
  formatLaneRecordMetaLine,
  formatLaneRoute,
  formatLaneVehicle,
  formatMoveMeta,
  formatPostingMeta,
} from './laneRecordFormatting';

export enum LaneRecordKind {
  Postings = 'postings',
  Moves = 'moves',
}

const ITEMS_PER_PAGE = 5;

const EMPTY_STATE_CLASSNAME =
  'border border-dashed border-neutral-300 bg-neutral-50 p-2 text-center text-neutral-500';

const SECONDARY_LINK_BUTTON_CLASSNAME =
  'text-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main';

const LANE_RECORD_GRID_STYLE = {
  gridTemplateColumns: 'minmax(0, 1fr) auto',
};

// Dates and expanded route share the same secondary line styling.
const LANE_RECORD_DETAIL_LINE_CLASSNAME =
  'min-w-0 break-words leading-tight text-neutral-500';

const UNAVAILABLE_COPY: Record<LaneRecordKind, string> = {
  [LaneRecordKind.Postings]: 'Comparable posting data is unavailable.',
  [LaneRecordKind.Moves]: 'Recent move data is unavailable.',
};

const EMPTY_COPY: Record<LaneRecordKind, string> = {
  [LaneRecordKind.Postings]: 'No active postings on this lane.',
  [LaneRecordKind.Moves]: 'No recent moves on this lane.',
};

type LaneRecordsProps = {
  rows: MaybeUndef<SuperDispatchLaneRecord[]>;
  kind: LaneRecordKind;
  isPending: boolean;
  formatAmount: ReturnType<typeof useQuickQuoteCurrency>['formatAmount'];
  formatPerMileRate: ReturnType<
    typeof useQuickQuoteCurrency
  >['formatPerMileRate'];
};

type LaneRecordRowProps = Omit<LaneRecordsProps, 'rows' | 'isPending'> & {
  row: SuperDispatchLaneRecord;
  expanded: boolean;
};

export function LaneRecords({
  rows,
  kind,
  isPending,
  formatAmount,
  formatPerMileRate,
}: LaneRecordsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState(false);

  const sortedRows = useMemo(
    () => sortLaneRecordsByRecency(rows ?? [], kind),
    [rows, kind]
  );

  const totalPages = Math.ceil(sortedRows.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const visibleRows = sortedRows.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
    setExpanded(false);
  }, [kind, rows]);

  useEffect(() => {
    setCurrentPage((page) => Math.max(1, Math.min(page, totalPages)));
  }, [totalPages]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    setExpanded(false);
  };

  // Postings come from a slower request. Show a skeleton until it finishes,
  // not an empty or unavailable message.
  if (isPending) {
    return <LaneRecordsSkeleton />;
  }

  if (rows == null) {
    return (
      <Typography variant='body-xxs' className={EMPTY_STATE_CLASSNAME}>
        {UNAVAILABLE_COPY[kind]}
      </Typography>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography variant='body-xxs' className={EMPTY_STATE_CLASSNAME}>
        {EMPTY_COPY[kind]}
      </Typography>
    );
  }

  const laneRecordRowProps = {
    kind,
    expanded,
    formatAmount,
    formatPerMileRate,
  };

  return (
    <div className='min-w-0'>
      <ul className='divide-y divide-neutral-100'>
        {visibleRows.map((row, index) => (
          <LaneRecordRow
            key={
              row.id ??
              `${row.pickupCity}-${row.deliveryCity}-${row.price}-${startIndex + index}`
            }
            row={row}
            {...laneRecordRowProps}
          />
        ))}
      </ul>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        startIndex={startIndex}
        endIndex={endIndex}
        totalItems={sortedRows.length}
      />
      <Flex justify='center'>
        <button
          type='button'
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
          aria-expanded={expanded}
          className={SECONDARY_LINK_BUTTON_CLASSNAME}
        >
          <Typography
            as='span'
            variant='body-xxs'
            weight='medium'
            className='text-inherit'
          >
            {expanded
              ? `Collapse ${kind === LaneRecordKind.Postings ? 'postings' : 'moves'}`
              : `Expand ${kind === LaneRecordKind.Postings ? 'postings' : 'moves'}`}
          </Typography>
        </button>
      </Flex>
    </div>
  );
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalItems,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalItems: number;
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  return (
    <Flex direction='col' align='center' gap='sm' className='mt-2'>
      <Flex align='center' justify='center' gap='sm' className='w-full'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          buttonNamePosthog={
            ButtonNamePosthog.QuickQuoteSuperDispatchLanePaginationPrevious
          }
          className='h-5 px-1.5 text-[10px]'
          type='button'
        >
          Prev
        </Button>

        <Flex align='center' gap='xs'>
          {pageNumbers.map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'outline'}
              size='sm'
              onClick={() => onPageChange(pageNum)}
              className='h-5 w-5 p-0 text-[10px]'
              buttonNamePosthog={
                ButtonNamePosthog.QuickQuoteSuperDispatchLanePaginationPage
              }
              type='button'
            >
              {pageNum}
            </Button>
          ))}
        </Flex>

        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          buttonNamePosthog={
            ButtonNamePosthog.QuickQuoteSuperDispatchLanePaginationNext
          }
          className='h-5 px-1.5 text-[10px]'
          type='button'
        >
          Next
        </Button>
      </Flex>
      <Typography variant='body-xxs' className='text-center text-neutral-500'>
        {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
      </Typography>
    </Flex>
  );
};

function visibleVehiclesForRow(
  vehicles: SuperDispatchLaneRecord['vehicles'],
  expanded: boolean
) {
  if (vehicles.length <= 2 || expanded) {
    return vehicles;
  }

  return vehicles.slice(0, 1);
}

function LaneRecordRow({
  row,
  kind,
  expanded,
  formatAmount,
  formatPerMileRate,
}: LaneRecordRowProps) {
  const vehicles = row.vehicles ?? [];
  const meta =
    kind === LaneRecordKind.Postings
      ? formatPostingMeta(row)
      : formatMoveMeta(row);
  const metaLine = formatLaneRecordMetaLine(meta, vehicles.length, expanded);
  const route = formatLaneRoute(row);
  const visibleVehicles = visibleVehiclesForRow(vehicles, expanded);

  return (
    <li className='py-1.5'>
      <Grid gapX='sm' style={LANE_RECORD_GRID_STYLE}>
        <Flex direction='col' gap='xs' className='min-w-0 flex-1'>
          {visibleVehicles.map((vehicle, vehicleIndex) => (
            <Typography
              key={`${vehicleIndex}-${formatLaneVehicle(vehicle)}`}
              variant='body-xxs'
              weight='semibold'
              className='min-w-0 break-keep leading-tight text-neutral-900'
              style={{ wordBreak: 'keep-all', overflowWrap: 'normal' }}
            >
              {formatLaneVehicle(vehicle)}
            </Typography>
          ))}
        </Flex>

        <Flex align='baseline' justify='end' gap='xs' className='tabular-nums'>
          <Typography
            variant='body-xxs'
            weight='semibold'
            className='text-neutral-900'
          >
            {formatAmount(row.price)}
          </Typography>
          {row.pricePerMile != null && (
            <Typography variant='body-xxs' className='text-neutral-500'>
              {`(${formatPerMileRate(row.pricePerMile, 2)}/mi)`}
            </Typography>
          )}
        </Flex>
      </Grid>

      {metaLine && (
        <Grid gapX='sm' style={LANE_RECORD_GRID_STYLE} className='mt-0.5'>
          <Typography
            variant='body-xxs'
            className={cn(LANE_RECORD_DETAIL_LINE_CLASSNAME, 'tabular-nums')}
          >
            {formatLaneMetaLine(metaLine)}
          </Typography>
        </Grid>
      )}

      {expanded && route && (
        <Grid gapX='sm' style={LANE_RECORD_GRID_STYLE} className='mt-0.5'>
          <Typography
            variant='body-xxs'
            className={LANE_RECORD_DETAIL_LINE_CLASSNAME}
          >
            {route}
          </Typography>
        </Grid>
      )}
    </li>
  );
}

function formatLaneMetaLine(metaLine: string) {
  const [start, end] = metaLine.split(' › ');

  if (!end) {
    return metaLine;
  }

  return (
    <>
      {start}
      <ChevronRightIcon
        className='mx-0.5 inline-block h-2.5 w-2.5 shrink-0 align-middle text-neutral-500'
        aria-hidden
      />
      {end}
    </>
  );
}

function LaneRecordsSkeleton() {
  return (
    <div className='space-y-1 py-0.5' aria-label='Loading lane records'>
      {[0, 1, 2].map((row) => (
        <Flex key={row} justify='between' gap='sm'>
          <Skeleton className='h-2.5 w-1/2' />
          <Skeleton className='h-2.5 w-14' />
        </Flex>
      ))}
    </div>
  );
}

function sortLaneRecordsByRecency(
  rows: SuperDispatchLaneRecord[],
  kind: LaneRecordKind
): SuperDispatchLaneRecord[] {
  return [...rows].sort((left, right) => {
    const leftDate = laneRecordRecencyDate(left, kind);
    const rightDate = laneRecordRecencyDate(right, kind);
    return rightDate.localeCompare(leftDate);
  });
}

function laneRecordRecencyDate(
  row: SuperDispatchLaneRecord,
  kind: LaneRecordKind
): string {
  if (kind === LaneRecordKind.Postings) {
    return row.postingDate ?? row.availableDate ?? '';
  }

  return row.dispatchedDate ?? row.deliveredDate ?? '';
}
